/**
 * The write-back queues read as groups — owner ruling DEC-105 (2026-10-01), `docs/adr/9042`.
 * Pure: the link's scope, the reads' parameters, the set an act takes and the act itself.
 *
 * A run's execution pack carries 5–7k lines, and the pack used to report them exported or executed
 * with ONE POST PER COMMAND; the backlog confirmed or cancelled one command at a time. DEC-105 puts
 * both on the alert groups' model (DEC-101, ADR 9040): the queue is read as groups — by store (the
 * default), by kind, by run — a group opens on its filter, and «Segna esportati», «Conferma eseguiti
 * come inviati» and the cancellation take a whole group in one request, with the count confirmed.
 *
 * **The URL is the scope** (CLAUDE.md §3.4). On `/inseason/write-back/pack` and `…/backlog`, beside
 * the run (`?runId=`): `?view=groups|lines`, `?by=`, `?group=` (the opened group's value), and on
 * the backlog `?sla=elapsed`. With no `?view=` the page opens on the groups once their read is
 * pinned, and on the lines it has today until then.
 *
 * **The pack is a run's; the backlog is the run's market's.** So the pack groups by store or kind —
 * its run is its scope, and a "by run" grouping of one run would be one line — and the backlog by
 * store, kind or run. An act on a pack group carries the run; on a backlog group, only a run group
 * does.
 *
 * **A group confirms executed only as sent** (`executedAsSent: true`): an executed quantity other
 * than the one sent stays a per-command report, in the command's own drawer.
 */

import { executionPack, marketExecutionPack } from '@/api/generated';
import { readGroupDimension, type RankedCapConfig } from '@/planning';

import {
  WRITE_BACK_GROUP_DIMENSIONS, WRITE_BACK_NO_STORE, WRITE_BACK_SUBJECT_KINDS,
  type WriteBackGroup, type WriteBackGroupDimension, type WriteBackGroupFilter, type WriteBackGroupQuery,
  type WriteBackGroupToState, type WriteBackGroupTransitionParams, type WriteBackStateCode, type WriteBackSubjectKind,
} from '@/api/pending/write-back-groups';
import type { PackLine } from './execution-pack';

export const WB_VIEW_PARAM = 'view';
export const WB_BY_PARAM = 'by';
export const WB_GROUP_PARAM = 'group';
export const WB_SLA_PARAM = 'sla';
export const WB_SLA_ELAPSED = 'elapsed';

/** The two write-back queues. */
export type WriteBackQueue = 'pack' | 'backlog';

export type WriteBackView = 'groups' | 'lines';

/** The dimensions each queue offers, its default first. */
export const QUEUE_DIMENSIONS: Readonly<Record<WriteBackQueue, readonly WriteBackGroupDimension[]>> = {
  pack: ['store', 'subject_kind'],
  backlog: WRITE_BACK_GROUP_DIMENSIONS,
};

/** Groups read per request. The groups are walked to the end and ranked here (ADR 9034). */
export const WRITE_BACK_GROUP_PAGE = 50;
/**
 * The most pages walked. A market has a few hundred stores, four kinds and tens of open runs; the
 * bound only stops a runaway cursor, and when it is hit the page says the list is partial.
 */
export const WRITE_BACK_GROUP_MAX_PAGES = 40;

/** Which view the page opens: the link wins; else groups once pinned, the lines until then. */
export function readWriteBackView(search: string, groupsPinned: boolean): WriteBackView {
  const view = new URLSearchParams(search).get(WB_VIEW_PARAM);
  if (view === 'groups' || view === 'lines') return view;
  return groupsPinned ? 'groups' : 'lines';
}

export interface WriteBackGroupScope {
  readonly by: WriteBackGroupDimension;
  /** The opened group's value; `null`: the inbox. */
  readonly group: string | null;
  /** The backlog narrowed to the groups with a command past its SLA. */
  readonly slaOnly: boolean;
}

/** The groups a link names. Pure. */
export function readWriteBackGroupScope(search: string, queue: WriteBackQueue): WriteBackGroupScope {
  const params = new URLSearchParams(search);
  const offered = QUEUE_DIMENSIONS[queue];
  const group = params.get(WB_GROUP_PARAM)?.trim() ?? '';
  return {
    by: readGroupDimension(params.get(WB_BY_PARAM), offered, offered[0]!),
    group: group === '' ? null : group,
    slaOnly: queue === 'backlog' && params.get(WB_SLA_PARAM) === WB_SLA_ELAPSED,
  };
}

/** The groups read: the pack's run, or the backlog's open commands of the market. */
export function writeBackGroupsQuery(
  queue: WriteBackQueue, scope: WriteBackGroupScope, country: string, runId: string, cursor?: string,
): WriteBackGroupQuery {
  return {
    group_by: scope.by,
    country_key: country,
    ...(queue === 'pack' ? { run_id: runId } : { state: 'open' }),
    ...(scope.slaOnly ? { sla_elapsed: true } : {}),
    ...(cursor === undefined ? {} : { cursor }),
    limit: WRITE_BACK_GROUP_PAGE,
  };
}

/** A kind group's key as the platform's closed vocabulary, or `null` for a word it does not hold. */
export function asSubjectKind(key: string): WriteBackSubjectKind | null {
  return (WRITE_BACK_SUBJECT_KINDS as readonly string[]).includes(key) ? (key as WriteBackSubjectKind) : null;
}

/**
 * The set an act takes: the queue's perimeter, the group's value and the state the act moves from.
 * Every field is a platform filter, so the set is reconstructible (DEC-90). `null` for a kind group
 * whose key is not a kind the pinned spec knows: dropping the filter would widen the set to every
 * kind, so nothing is composed.
 */
export function writeBackGroupFilter(
  queue: WriteBackQueue, by: WriteBackGroupDimension, key: string, country: string, runId: string, fromState: WriteBackStateCode,
): WriteBackGroupFilter | null {
  const run = by === 'run' ? key : queue === 'pack' ? runId : null;
  const subjectKind = by === 'subject_kind' ? asSubjectKind(key) : null;
  if (by === 'subject_kind' && subjectKind === null) return null;
  return {
    countryKey: country,
    ...(run === null ? {} : { runId: run }),
    ...(by === 'store' ? { storeKey: key } : {}),
    ...(subjectKind === null ? {} : { subjectKind }),
    fromState,
  };
}

/**
 * The filter value that selects a group: its key, or `-` for the store grouping's no-value bucket —
 * the commands with no store (price steps), which the platform selects with `store_key=-`
 * (bata-platform ADR 6054). A kind or a run always has a value, so their null bucket has no filter.
 */
export function groupFilterValue(group: Pick<WriteBackGroup, 'dimension' | 'dimensionKey'>): string | null {
  const key = group.dimensionKey;
  if (key !== null && key.trim() !== '') return key;
  return group.dimension === 'store' ? WRITE_BACK_NO_STORE : null;
}

/** Whether a group's value is the no-store bucket, by its key or by the `-` that selects it. */
export function isNoStore(by: WriteBackGroupDimension, key: string | null): boolean {
  return by === 'store' && (key === null || key.trim() === '' || key === WRITE_BACK_NO_STORE);
}

/** The link that opens a group, or `null` when no filter selects it. */
export function writeBackGroupHref(pathname: string, search: string, group: WriteBackGroup): string | null {
  const key = groupFilterValue(group);
  if (key === null) return null;
  const params = new URLSearchParams(search);
  params.set(WB_VIEW_PARAM, 'groups');
  params.set(WB_BY_PARAM, group.dimension);
  params.set(WB_GROUP_PARAM, key);
  return `${pathname}?${params.toString()}`;
}

/** The inbox a group page came from: the same link without the group. */
export function writeBackInboxHref(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete(WB_GROUP_PARAM);
  params.set(WB_VIEW_PARAM, 'groups');
  return `${pathname}?${params.toString()}`;
}

// ── the acts ─────────────────────────────────────────────────────────────────

/** The acts on a write-back group. */
export type WriteBackGroupActKind = 'export' | 'confirmAsSent' | 'cancel';

/** Each queue's acts: the pack exports and confirms; the backlog confirms and cancels. */
export const QUEUE_ACTS: Readonly<Record<WriteBackQueue, readonly WriteBackGroupActKind[]>> = {
  pack: ['export', 'confirmAsSent'],
  backlog: ['confirmAsSent', 'cancel'],
};

/** The state an act moves FROM and TO. A cancellation's from-state is the planner's choice. */
export const ACT_TRANSITION: Readonly<Record<'export' | 'confirmAsSent', { readonly from: WriteBackStateCode; readonly to: WriteBackGroupToState }>> = {
  export: { from: 'approved', to: 'dispatched' },
  // As sent: a partly executed command already has another quantity, so only `dispatched` moves.
  confirmAsSent: { from: 'dispatched', to: 'acknowledged' },
};

/** The states a command may be cancelled from — still to execute (bata-platform ADR 6105). */
export const CANCEL_FROM_STATES: readonly WriteBackStateCode[] = ['approved', 'dispatched', 'partially_executed'];

/** How many of a group's commands an act would take from `fromState`. */
export function countInState(group: Pick<WriteBackGroup, 'byState'>, fromState: string): number {
  const count = group.byState[fromState];
  return typeof count === 'number' && Number.isInteger(count) && count > 0 ? count : 0;
}

export interface WriteBackActInput {
  readonly filter: WriteBackGroupFilter;
  readonly toState: WriteBackGroupToState;
  readonly expectedCount: number;
  readonly reasonCode?: string | null;
  readonly commentary?: string;
}

/**
 * The composed transition, or `null` while something it needs is missing. A confirmation is sent
 * `executedAsSent: true` — the only one a group may make; a cancellation needs its governed reason.
 */
export function composeWriteBackTransition(input: WriteBackActInput): WriteBackGroupTransitionParams | null {
  const { filter, toState, expectedCount } = input;
  if (!Number.isInteger(expectedCount) || expectedCount <= 0) return null;
  const commentary = (input.commentary ?? '').trim();
  const note = commentary === '' ? {} : { commentary };
  switch (toState) {
    case 'dispatched':
      return { body: { filter, toState, expectedCount, ...note } };
    case 'acknowledged':
      return { body: { filter, toState, expectedCount, executedAsSent: true, ...note } };
    case 'cancelled': {
      const reason = (input.reasonCode ?? '').trim();
      if (reason === '') return null;
      return { body: { filter, toState, expectedCount, reasonCode: reason, ...note } };
    }
    default:
      return null;
  }
}

// ── the group's lines ────────────────────────────────────────────────────────

/**
 * Whether the pinned pack reads take `store_key` and `subject_kind` (the platform's DEC-105 PR adds
 * them; the pin to #496's spec carries them). Without them a store group's lines cannot be read by
 * the store the platform grouped on, and the group page says so rather than guessing which of a
 * line's stores it meant.
 */
export const PACK_READS_TAKE_GROUP_FILTERS = (['store_key', 'subject_kind'] as const).every((param) => (
  (executionPack.queryParams as readonly string[]).includes(param)
  && (marketExecutionPack.queryParams as readonly string[]).includes(param)));

/** The extra filters a group's lines are read with, when the reads take them. */
export function groupLinesQuery(
  by: WriteBackGroupDimension, key: string, takesFilters = PACK_READS_TAKE_GROUP_FILTERS,
): { readonly store_key?: string; readonly subject_kind?: WriteBackSubjectKind } {
  if (!takesFilters) return {};
  if (by === 'store') return { store_key: key };
  // A word the pinned spec does not hold is not sent; `lineInGroup` still keeps the kind's lines.
  const kind = by === 'subject_kind' ? asSubjectKind(key) : null;
  if (kind !== null) return { subject_kind: kind };
  return {};
}

/**
 * Whether a line read belongs to the group, for what the read could not filter itself: kind and run
 * are each a line's own field. A store group needs the read's filter (see above); `null` here says so.
 */
export function lineInGroup(
  line: Pick<PackLine, 'subject'> & { readonly provenance: { readonly run_id: string } },
  by: WriteBackGroupDimension, key: string, takesFilters = PACK_READS_TAKE_GROUP_FILTERS,
): boolean | null {
  if (by === 'subject_kind') return line.subject === key;
  if (by === 'run') return line.provenance.run_id === key;
  return takesFilters ? true : null;
}

// ── the ranking ──────────────────────────────────────────────────────────────

const at = (iso: string): number | null => {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The groups' ranking (ADR 9034), on `?groups.rank=`. The backlog is exception-first: past SLA,
 * then the oldest; the pack opens on what is left to export.
 */
export function groupRankConfig(queue: WriteBackQueue): RankedCapConfig<WriteBackGroup> {
  const oldestFirst = { value: (group: WriteBackGroup) => at(group.oldestAt), direction: 'asc' as const };
  return {
    namespace: 'groups',
    defaultRank: queue === 'backlog' ? 'sla' : 'toExport',
    rankOptions: [
      { id: 'sla', labelKey: 'inseason.writeBackGroups.rank.sla', value: (group) => group.slaElapsedCount, direction: 'desc', tieBreak: oldestFirst },
      { id: 'age', labelKey: 'inseason.writeBackGroups.rank.age', value: (group) => group.oldestAt, direction: 'asc' },
      { id: 'toExport', labelKey: 'inseason.writeBackGroups.rank.toExport', value: (group) => countInState(group, 'approved'), direction: 'desc', tieBreak: oldestFirst },
      { id: 'commands', labelKey: 'inseason.writeBackGroups.rank.commands', value: (group) => group.commandCount, direction: 'desc' },
      { id: 'units', labelKey: 'inseason.writeBackGroups.rank.units', value: (group) => group.units, direction: 'desc' },
    ],
  };
}

/** The lines' ranking inside a group, on `?rank=`: the oldest first, or the most units. */
export function lineRankConfig(): RankedCapConfig<PackLine> {
  return {
    defaultRank: 'age',
    rankOptions: [
      { id: 'age', labelKey: 'inseason.writeBackGroups.rank.age', value: (line) => line.createdAt ?? line.stateObservedAt, direction: 'asc' },
      { id: 'units', labelKey: 'inseason.writeBackGroups.rank.units', value: (line) => line.authorisedUnits ?? line.units, direction: 'desc' },
    ],
    filters: [
      { id: 'state', labelKey: 'inseason.executionPack.column.state', kind: 'select', value: (line) => line.state },
    ],
  };
}

/** The order a group's states are said in: the way a command moves, then the rest. */
export const STATE_ORDER: readonly string[] = [
  'approved', 'dispatched', 'partially_executed', 'acknowledged', 'executed', 'delivered', 'rejected', 'cancelled', 'unknown',
];

/** A group's non-zero states in that order, an unrecognised one after them by name. */
export function statesOf(group: Pick<WriteBackGroup, 'byState'>): readonly { readonly state: string; readonly count: number }[] {
  const known = STATE_ORDER.filter((state) => countInState(group, state) > 0);
  const others = Object.keys(group.byState).filter((state) => !STATE_ORDER.includes(state) && countInState(group, state) > 0).sort();
  return [...known, ...others].map((state) => ({ state, count: countInState(group, state) }));
}
